import { create } from 'zustand';

import {
  createWorld,
  getWorld,
  joinWorld,
  listWorlds,
  type WorldInfoJson,
} from '../platform/api';
import { useToasts } from '../toasts';

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 30_000;

export interface WorldsState {
  worlds: WorldInfoJson[];
  pollingWorldId: number | null;
  pollingStatus: string | null;
  loadWorlds(): Promise<void>;
  createAndPoll(claimId: number, name: string, visibility?: string, maxPlayers?: number): Promise<WorldInfoJson | null>;
  wakeAndPoll(worldId: number): Promise<WorldInfoJson | null>;
  getWorldForClaim(claimId: number): WorldInfoJson | undefined;
  stopPolling(): void;
}

export const useWorlds = create<WorldsState>((set, get) => ({
  worlds: [],
  pollingWorldId: null,
  pollingStatus: null,

  async loadWorlds() {
    try {
      const worlds = await listWorlds('owned');
      set({ worlds });
    } catch (e) {
      useToasts.getState().push('error', `Failed to load worlds: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async createAndPoll(claimId, name, visibility = 'Public', maxPlayers = 20) {
    try {
      const world = await createWorld(claimId, name, visibility, maxPlayers);
      set((s) => ({
        worlds: [...s.worlds, world],
        pollingWorldId: world.world_id,
        pollingStatus: world.status,
      }));

      if (world.status === 'Active') return world;

      const result = await pollUntilActive(world.world_id, set);
      return result;
    } catch (e) {
      useToasts.getState().push('error', `Failed to create world: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  },

  async wakeAndPoll(worldId) {
    try {
      await joinWorld(worldId);
      set({ pollingWorldId: worldId, pollingStatus: 'Waking' });

      const result = await pollUntilActive(worldId, set);
      return result;
    } catch (e) {
      useToasts.getState().push('error', `Failed to wake world: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  },

  getWorldForClaim(claimId) {
    return get().worlds.find((w) => w.claim_id === claimId);
  },

  stopPolling() {
    set({ pollingWorldId: null, pollingStatus: null });
  },
}));

async function pollUntilActive(
  worldId: number,
  set: (fn: (s: WorldsState) => Partial<WorldsState>) => void,
): Promise<WorldInfoJson | null> {
  const start = Date.now();

  while (Date.now() - start < POLL_TIMEOUT_MS) {
    await sleep(POLL_INTERVAL_MS);

    try {
      const world = await getWorld(worldId);
      set((s) => ({
        worlds: s.worlds.map((w) => (w.world_id === worldId ? world : w)),
        pollingStatus: world.status,
      }));

      if (world.status === 'Active') {
        set(() => ({ pollingWorldId: null, pollingStatus: null }));
        return world;
      }

      if (world.status === 'Failed' || world.status === 'Deleted') {
        set(() => ({ pollingWorldId: null, pollingStatus: null }));
        useToasts.getState().push('error', `World ${world.status.toLowerCase()}`);
        return null;
      }
    } catch {
      // Transient fetch error — keep polling
    }
  }

  set(() => ({ pollingWorldId: null, pollingStatus: null }));
  useToasts.getState().push('error', 'World start timed out. Try again.');
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
